import { ref, computed, watch, type Ref } from 'vue';
import type {
  BaseEntity,
  EntitySearchConfig,
  EntitySearchResult,
  EntityGroup,
  GroupedSearchResults,
  PaginationParams,
  PaginatedResponse,
  SearchField,
} from './types';
import { createFuseOptions } from '../useSearchPatternUtils';

export interface UseEntitySearchOptions<T extends BaseEntity> {
  config: EntitySearchConfig<T>;
  searchQuery: Ref<string>;
  parentId?: Ref<string | undefined>;
  parentName?: Ref<string | undefined>;
  pageSize?: number;
}

export interface UseEntitySearchReturn<T extends BaseEntity> {
  // Data
  items: Ref<T[]>;
  groupedItems: Ref<EntityGroup<T>[]>;

  // Search
  searchPattern: Ref<Record<string, unknown> | null>;
  fuseOptions: ReturnType<typeof createFuseOptions>;

  // API search for hybrid mode
  apiSearchUnrelated: (query: string, pagination: PaginationParams) => Promise<PaginatedResponse<T>>;

  // Formatting
  formatResult: (item: T) => EntitySearchResult<T>;

  // Loading
  isLoading: Ref<boolean>;
  isLoadingMore: Ref<boolean>;
  hasMore: Ref<boolean>;

  // Actions
  loadItems: () => Promise<void>;
  loadMoreItems: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useEntitySearch<T extends BaseEntity>(
  options: UseEntitySearchOptions<T>
): UseEntitySearchReturn<T> {
  const { config, searchQuery, parentId, parentName, pageSize = 50 } = options;

  // State
  const relatedItems = ref<T[]>([]) as Ref<T[]>;
  const paginatedItems = ref<T[]>([]) as Ref<T[]>;
  const isLoading = ref(false);
  const isLoadingMore = ref(false);
  const currentPage = ref(1);
  const hasMore = ref(true);
  // Tracks whether loadItems() has been called at least once (e.g. on first user interaction).
  // Prevents the parentId watcher from triggering requests before the user opens the selector.
  const hasBeenActivated = ref(false);

  // Computed: All items (related + unrelated, deduplicated)
  const items = computed<T[]>(() => {
    const relatedIds = new Set(relatedItems.value.map((item) => (item as any).id));
    const unrelated = paginatedItems.value.filter((item) => !relatedIds.has((item as any).id));
    return [...relatedItems.value, ...unrelated];
  });

  // Computed: Grouped items for autocomplete display
  const groupedItems = computed<EntityGroup<T>[]>(() => {
    const relatedIds = new Set(relatedItems.value.map((item) => (item as any).id));
    const unrelated = paginatedItems.value.filter((item) => !relatedIds.has((item as any).id));

    const groups: EntityGroup<T>[] = [];

    if (relatedItems.value.length > 0) {
      groups.push({
        header: getRelatedHeader(),
        items: relatedItems.value,
      });
    }

    if (unrelated.length > 0 || relatedItems.value.length === 0) {
      groups.push({
        header: getUnrelatedHeader(),
        items: unrelated,
      });
    }

    return groups;
  });

  // Helper: Get header text for related items
  function getRelatedHeader(): string {
    const entityName = getEntityDisplayName(config.entityType);
    const name = parentName?.value;

    // Show contextual header with parent name if available
    if (name) {
      // Use singular for organisation since there's only one related org
      if (config.entityType === 'organisation') {
        return `${entityName} from ${name}`;
      }
      return `${entityName}s from ${name}`;
    }

    // Fallback to generic header
    if (config.entityType === 'organisation') {
      return `Related ${entityName}`;
    }
    return `Related ${entityName}s`;
  }

  // Helper: Get header text for unrelated items
  function getUnrelatedHeader(): string {
    const entityName = getEntityDisplayName(config.entityType);
    return relatedItems.value.length > 0 ? `Other ${entityName}s` : `All ${entityName}s`;
  }

  // Helper: Get display name for entity type
  function getEntityDisplayName(type: string): string {
    const names: Record<string, string> = {
      organisation: 'Organisation',
      unit: 'Unit',
      site: 'Site',
      contact_person: 'Contact Person',
    };
    return names[type] || type;
  }

  // Computed: Search pattern for Fuse.js
  const searchPattern = computed(() => {
    if (!searchQuery.value || !searchQuery.value.trim()) {
      return null;
    }

    return {
      $or: config.searchFields.map((field) => ({
        [field.name]: searchQuery.value,
      })),
    };
  });

  // Fuse.js options
  const fuseOptions = createFuseOptions(config.searchFields);

  // Load items (initial load)
  async function loadItems(): Promise<void> {
    hasBeenActivated.value = true;
    isLoading.value = true;
    currentPage.value = 1;
    hasMore.value = true;

    try {
      // Load related items if we have a parent ID
      if (parentId?.value && config.fetchByParent) {
        const response = await config.fetchByParent(parentId.value, { page: 1, limit: pageSize });
        relatedItems.value = response.data;
      } else {
        relatedItems.value = [];
      }

      // Load paginated items (all items)
      const response = await config.fetchAll({ page: 1, limit: pageSize });
      paginatedItems.value = response.data;
      hasMore.value = response.hasMore;
    } catch (error) {
      console.error(`Failed to load ${config.entityType}s:`, error);
      relatedItems.value = [];
      paginatedItems.value = [];
      hasMore.value = false;
    } finally {
      isLoading.value = false;
    }
  }

  // Load more items (pagination)
  async function loadMoreItems(): Promise<void> {
    if (isLoadingMore.value || !hasMore.value) {
      return;
    }

    isLoadingMore.value = true;

    try {
      currentPage.value += 1;
      const response = await config.fetchAll({ page: currentPage.value, limit: pageSize });
      paginatedItems.value = [...paginatedItems.value, ...response.data];
      hasMore.value = response.hasMore;
    } catch (error) {
      console.error(`Failed to load more ${config.entityType}s:`, error);
      currentPage.value -= 1; // Rollback
    } finally {
      isLoadingMore.value = false;
    }
  }

  // Refresh items
  async function refresh(): Promise<void> {
    await loadItems();
  }

  // API search for unrelated items (hybrid mode)
  async function apiSearchUnrelated(
    query: string,
    pagination: PaginationParams
  ): Promise<PaginatedResponse<T>> {
    if (!config.apiSearch) {
      return { data: [], hasMore: false };
    }

    try {
      // Exclude related items from API search results
      const relatedIds = relatedItems.value.map((item) => (item as any).id).filter(Boolean);
      const response = await config.apiSearch(query, pagination, relatedIds);

      // Filter out any related items that might have slipped through
      const filteredData = response.data.filter(
        (item) => !relatedIds.includes((item as any).id)
      );

      return {
        data: filteredData,
        hasMore: response.hasMore,
      };
    } catch (error) {
      console.error(`API search failed for ${config.entityType}s:`, error);
      return { data: [], hasMore: false };
    }
  }

  // Format result with related indicator
  function formatResult(item: T): EntitySearchResult<T> {
    const relatedIds = new Set(relatedItems.value.map((i) => (i as any).id));
    const isRelated = relatedIds.has((item as any).id);
    return config.formatResult(item, isRelated);
  }

  // Watch for parent ID changes and reload — but only if items have been loaded at least once.
  // This prevents eager requests when the ticket dialog initializes and sets parentId
  // before the user has interacted with the selector.
  if (parentId) {
    watch(
      parentId,
      async (newId, oldId) => {
        if (newId !== oldId && hasBeenActivated.value) {
          await loadItems();
        }
      },
      { immediate: false }
    );
  }

  return {
    items,
    groupedItems,
    searchPattern,
    fuseOptions,
    apiSearchUnrelated,
    formatResult,
    isLoading,
    isLoadingMore,
    hasMore,
    loadItems,
    loadMoreItems,
    refresh,
  };
}
